import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { LoginScreen } from '../screens/LoginScreen'
import { VerifyScreen } from '../screens/VerifyScreen'
import { WelcomeScreen } from '../screens/WelcomeScreen'
import { CompanyDetailsScreen } from '../screens/CompanyDetailsScreen'
import { CompanyStructureScreen } from '../screens/CompanyStructureScreen'
import { ProcessingBreakdownScreen } from '../screens/ProcessingBreakdownScreen'
import { ReviewScreen } from '../screens/ReviewScreen'
import { SuccessScreen } from '../screens/SuccessScreen'
import { MyApplicationsScreen } from '../screens/MyApplicationsScreen'
import { ApplicationDetailScreen } from '../screens/ApplicationDetailScreen'
import { AuthErrorScreen } from '../screens/AuthErrorScreen'
import { BootGate, RedirectIfAuthed, RequireAuth, RequireDraft, SessionProvider, useSession } from './session'
import { useAutoSave } from './useAutoSave'
import type { SessionResponse } from '../api/types'

// Single persistent auto-save instance for the whole authed session — mounted
// outside the routes so a pending debounced save survives screen changes.
function AutoSaveAgent() {
  useAutoSave()
  return null
}

function AutoSaveBridge() {
  const { status } = useSession()
  return status === 'authed' ? <AutoSaveAgent /> : null
}

// SPA navigations keep the previous scroll position, so clicking Continue at
// the bottom of a step would land the user at the bottom of the next one.
// Jump to the top on every route change instead (Back and stepper jumps too).
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

// The applicant flow is mounted under /apply; the basename keeps every
// screen's absolute navigate('/welcome') etc. working unchanged.
export function ApplyApp({ magicSession }: { magicSession: Promise<SessionResponse> | null }) {
  return (
    <BrowserRouter basename="/apply">
      <SessionProvider magicSession={magicSession}>
        <AutoSaveBridge />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<BootGate />} />
          <Route path="/login" element={<RedirectIfAuthed><LoginScreen /></RedirectIfAuthed>} />
          <Route path="/verify" element={<RedirectIfAuthed><VerifyScreen /></RedirectIfAuthed>} />
          <Route path="/auth/error" element={<AuthErrorScreen />} />
          <Route path="/welcome" element={<RequireDraft><WelcomeScreen /></RequireDraft>} />
          <Route path="/company-details" element={<RequireDraft><CompanyDetailsScreen /></RequireDraft>} />
          <Route path="/company-structure" element={<RequireDraft><CompanyStructureScreen /></RequireDraft>} />
          <Route path="/processing-breakdown" element={<RequireDraft><ProcessingBreakdownScreen /></RequireDraft>} />
          <Route path="/review" element={<RequireDraft><ReviewScreen /></RequireDraft>} />
          <Route path="/success" element={<RequireAuth><SuccessScreen /></RequireAuth>} />
          <Route path="/applications" element={<RequireAuth><MyApplicationsScreen /></RequireAuth>} />
          <Route path="/applications/:id" element={<RequireAuth><ApplicationDetailScreen /></RequireAuth>} />
          {/* Pre-multi-application bookmarks and emails still point here. */}
          <Route path="/history" element={<Navigate to="/applications" replace />} />
          <Route path="*" element={<BootGate />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  )
}
